import { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Fetch effective (post-promo, post-loyalty, post-referral) pricing for
 * Basic and Premium tiers from `/api/checkout/quote`. Works for both
 * authenticated and anonymous callers — the endpoint is public with
 * optional auth, so it also drives the onboarding tier picker before
 * the user has signed up.
 *
 * Returns `{ basic, premium, loaded }`. Each tier is either `null` (still
 * loading / failed) or `{ effective, base, discounted, promoStage, reasons }`.
 * `discounted` is `true` when `effective !== base` — the UI uses this to
 * decide whether to strike through the base price.
 *
 * Fallback: if the API fails the hook returns the base amounts from the
 * server-side `PACKAGES` constant so the UI never shows a bare `$…`. This
 * intentionally does NOT set `discounted` on fallback (the app-wide
 * "current promo" is unknown at that point).
 */
const FALLBACK = { basic: 750, premium: 1250 };

function shape(quote, fallbackAmount) {
  if (!quote) {
    return {
      effective: fallbackAmount,
      base: fallbackAmount,
      discounted: false,
      promoStage: null,
      reasons: [],
    };
  }
  const eff = Number(quote.effective_amount ?? fallbackAmount);
  const base = Number(quote.base_amount ?? eff);
  return {
    effective: eff,
    base,
    discounted: eff !== base,
    promoStage: quote.promo_stage || null,
    reasons: quote.reasons || [],
  };
}

export default function useEffectivePricing() {
  const [state, setState] = useState({ basic: null, premium: null, loaded: false });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [basicRes, premRes] = await Promise.all([
        api.get("/checkout/quote", { params: { package_id: "basic" } }).catch(() => null),
        api.get("/checkout/quote", { params: { package_id: "premium" } }).catch(() => null),
      ]);
      if (cancelled) return;
      setState({
        basic: shape(basicRes?.data, FALLBACK.basic),
        premium: shape(premRes?.data, FALLBACK.premium),
        loaded: true,
      });
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

/** Format `$1,250` or `$0` cleanly. Trailing `.00` is dropped. */
export function formatPrice(amount) {
  if (amount === 0) return "Free";
  const rounded = Math.round(amount * 100) / 100;
  const whole = Math.floor(rounded);
  const cents = Math.round((rounded - whole) * 100);
  const wholeStr = whole.toLocaleString("en-US");
  return cents === 0 ? `$${wholeStr}` : `$${wholeStr}.${String(cents).padStart(2, "0")}`;
}
